import * as React from 'react';

import { ForwardRefWithStaticComponents } from '@/types';
import { Button, ButtonProps } from '@/components/Button';
import { PaginationProps } from '@/components/Pagination';

import { Checkbox } from './components/Checkbox';
import { TableBody } from './components/TableBody';
import { TableCell } from './components/TableCell';
import { TableHead } from './components/TableHead';
import { TableHeadCell } from './components/TableHeadCell';
import { TablePagination, TablePaginationProps } from './components/TablePagination';
import { TableProvider } from './components/TableProvider';
import { TableRow } from './components/TableRow';
import {
  TableProviderProps,
  SelectionType,
  SELECT_ALL_ITEMS,
  useTableSelectionChange,
  useTableValue,
} from './utils/table';
import { classNames } from '@/utils';

export interface NativeTableProps extends React.ComponentProps<'table'> {
  /** The table content (optional). */
  children?: React.ReactNode;
  /** Whether the table rows should be selectable (optional). */
  selectable?: boolean;
  /** Whether the table is browsable (optional). */
  browsable?: boolean;
  /** The actions to be displayed when a row in the table is selected (optional). */
  selectActions?: React.ReactNode;
}

/**
 * This interface defines the props that can be passed to a table component that includes pagination.
 * It extends the `NativeTableProps` interface, and includes additional props related to pagination.
 */
interface TableBaseWithPagination extends NativeTableProps, Omit<TablePaginationProps, keyof PaginationProps> {
  /** A boolean indicating whether pagination should be displayed (required). */
  showPagination: true;
  /** A function that is called when the "Next" button is clicked (required). */
  onNextPage: () => void;
  /** A function that is called when the "Previous" button is clicked (required). */
  onPreviousPage: () => void;
  /** A boolean indicating whether there is a next page (required). */
  hasNextPage: boolean;
  /** A boolean indicating whether there is a previous page (required). */
  hasPreviousPage: boolean;
  /** The props to be applied to the "Next" button (optional). */
  nextPageButtonProps: ButtonProps;
  /** The props to be applied to the "Previous" button (optional). */
  previousPageButtonProps: ButtonProps;
  /** CSS class names to be applied to the pagination element (optional). */
  paginationClassName?: string;
}

/**
 * This interface defines the props that can be passed to a table component that does not include pagination.
 * It extends the `NativeTableProps` interface, and does not include any additional props.
 */
interface TableBaseWithoutPagination extends NativeTableProps {
  /** A boolean indicating whether pagination should be displayed (optional). */
  showPagination?: never;
  /** A function that is called when the "Next" button is clicked (optional). */
  onNextPage?: never;
  /** A function that is called when the "Previous" button is clicked (optional). */
  onPreviousPage?: never;
  /** A boolean indicating whether there is a next page (optional). */
  hasNextPage?: never;
  /** A boolean indicating whether there is a previous page (optional). */
  hasPreviousPage?: never;
  /** The props to be applied to the "Next" button (optional). */
  nextPageButtonProps?: never;
  /** The props to be applied to the "Previous" button (optional). */
  previousPageButtonProps?: never;
  /** CSS class names to be applied to the pagination element (optional). */
  paginationClassName?: never;
}

/**
 * This type defines the props that can be passed to a table component.
 * It includes the `TableBaseWithPagination` and `TableBaseWithoutPagination` interfaces,
 * and ensures that only one set of props is used at a time.
 */
export type TableBaseProps = TableBaseWithPagination | TableBaseWithoutPagination;

type TableComponent = ForwardRefWithStaticComponents<
  TableBaseProps,
  {
    Head: typeof TableHead;
    Body: typeof TableBody;
    Row: typeof TableRow;
    HeadCell: typeof TableHeadCell;
    Cell: typeof TableCell;
  }
>;

const TableBase: TableComponent = React.forwardRef<HTMLTableElement, TableBaseProps>((props, ref) => {
  const { children, showPagination, selectActions, browsable = false } = props;
  const { bulkSelectState, itemCount, selectedItemsCount, selectable } = useTableValue();
  const handleSelectionChange = useTableSelectionChange();

  const handleSelectAllItems = React.useCallback(() => {
    handleSelectionChange(selectedItemsCount === SELECT_ALL_ITEMS ? SelectionType.Page : SelectionType.All);
  }, [handleSelectionChange, selectedItemsCount]);

  const handleTogglePage = React.useCallback(() => {
    handleSelectionChange(SelectionType.Page);
  }, [bulkSelectState, handleSelectionChange]);

  const bodyMarkup = (
    <div className="table-container">
      <table ref={ref} className={classNames('datatable', { selectable, browsable })}>
        {children}
      </table>
    </div>
  );
  return (
    <section className="table-section">
      <form>
        {bulkSelectState && (
          <div className="table-actions visible">
            <div className="items-group justify" style={{ alignItems: 'center' }}>
              <div className="items-group">
                <Checkbox
                  labelClassName="items-group"
                  onChange={() => handleTogglePage()}
                  className={selectedItemsCount !== SELECT_ALL_ITEMS && selectedItemsCount < itemCount ? 'neutral' : ''}
                  checked
                  label={
                    <p data-role="input-value">{`${selectedItemsCount} ${
                      selectedItemsCount === SELECT_ALL_ITEMS || selectedItemsCount > 1 ? 'items' : 'item'
                    } selected`}</p>
                  }
                />
                {selectActions}
              </div>

              <div className="action">
                <Button variant="inline" onClick={() => handleSelectAllItems()}>
                  Select all items
                </Button>
              </div>
            </div>
          </div>
        )}
        {bodyMarkup}
        {showPagination && (
          <TablePagination
            hasNext={props.hasNextPage}
            hasPrevious={props.hasPreviousPage}
            onNext={props.onNextPage}
            onPrevious={props.onPreviousPage}
            nextButtonProps={props.nextPageButtonProps}
            previousButtonProps={props.previousPageButtonProps}
            className={props.paginationClassName}
          />
        )}
      </form>
    </section>
  );
}) as any;

export type TableProps = TableBaseProps & TableProviderProps;

export function Table({
  children,
  selectable = false,
  itemCount,
  selectedItemsCount = 0,
  onSelectionChange,
  ...tableBaseProps
}: TableProps) {
  return (
    <TableProvider
      selectable={selectable}
      itemCount={itemCount}
      selectedItemsCount={selectedItemsCount}
      onSelectionChange={onSelectionChange}
    >
      <TableBase {...tableBaseProps}>{children}</TableBase>
    </TableProvider>
  );
}

Table.displayName = 'Table';

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.HeadCell = TableHeadCell;
Table.Cell = TableCell;
